import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageOrderByOptions from './useManageOrderByOptions';
import OrderByOptionService from './OrderByOptionService';
import OrderByOptionForm from './OrderByOptionForm';
import { feeSummary, toRows, rowsFromLegacy, toTiers, rowsError } from './feeTiers';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '780px',
    maxWidth: '96vw',
    maxHeight: '90vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

// Fee: feeTierRows is the editor's working copy (strings); feeTiers is sent on save.
// The old feeRate / feeFixedJpy / feePercentage are no longer edited here (P2) — they
// are shown read-only in the form until the P2.4 migration moves everyone to tiers.
const BLANK = {
  id: '',
  value: '',
  label: '',
  sortOrder: 0,
  isActive: true,
  feeTierRows: [],
  specialFeeTierRows: [],
  orderByType: '',
  dailyOrderLimit: '',
  dailyAmountLimitJpy: '',
  isAmountLimitStrict: true,
  contactInfo: '',
  extendedDailyAmountLimitJpy: '',
  limitExtendHourThai: '',
  carryOverBalanceJpy: 0,
  isAuctionAccount: false,
  auctionForShop: '',
  mercariUserId: '',
  isUrgencyAnchor: false,
  isSmileNameInputRequire: true,
  lineGroupId: '',
  lineGroupName: '',
  dispatchLineId: '',
  dispatchSheetId: '',
  dispatchSheetTab: '',
  balanceSheetId: '',
  balanceSheetTab: '',
  googleSheetId: '',
  googleSheetTab: '',
  sheetSource: 'line',
  requireLineGroupLink: false,
  trackingSyncEnabled: false,
  trackingUrlColumn: '',
  trackingColumn: '',
  trackingDataStartRow: 2,
};

const DELETE_ITEMS = [{ title: 'Label', name: 'label', type: 'text' }];

const OrderByOptionSetting = () => {
  const [mode, setMode] = useState('register');
  const [modalTitle, setModalTitle] = useState('Register Order By Option');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [togglingBanId, setTogglingBanId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [orderByOption, setOrderByOption] = useState(BLANK);

  const {
    orderByOptions,
    isLoading,
    error,
    addOrderByOption,
    editOrderByOption,
    deleteOrderByOption,
    toggleSuspend,
    refreshOrderByOptions
  } = useManageOrderByOptions();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOrderByOption({
      ...orderByOption,
      [name]: type === 'checkbox'
        ? checked
        : (type === 'number'
          ? (value === '' ? '' : Number(value))
          : value)
    });
  };

  const openForm = (isEdit, id) => {
    if (isEdit) {
      const item = orderByOptions.find((o) => o.id === id);
      if (item) {
        setOrderByOption({
          id: item.id,
          value: item.value || '',
          label: item.label || '',
          sortOrder: item.sortOrder ?? 0,
          isActive: item.isActive !== undefined ? item.isActive : true,
          feeRate: item.feeRate ?? null,
          feeFixedJpy: item.feeFixedJpy ?? null,
          feePercentage: item.feePercentage ?? null,
          feeTierRows: Array.isArray(item.feeTiers) && item.feeTiers.length > 0 ? toRows(item.feeTiers) : rowsFromLegacy(item),
          specialFeeTierRows: Array.isArray(item.specialFeeTiers) && item.specialFeeTiers.length > 0 ? toRows(item.specialFeeTiers) : [],
          orderByType: item.orderByType ?? '',
          dailyOrderLimit: item.dailyOrderLimit ?? '',
          dailyAmountLimitJpy: item.dailyAmountLimitJpy ?? '',
          isAmountLimitStrict: item.isAmountLimitStrict !== undefined ? item.isAmountLimitStrict : true,
          contactInfo: item.contactInfo ?? '',
          extendedDailyAmountLimitJpy: item.extendedDailyAmountLimitJpy ?? '',
          limitExtendHourThai: item.limitExtendHourThai ?? '',
          carryOverBalanceJpy: item.carryOverBalanceJpy ?? 0,
          isAuctionAccount: item.isAuctionAccount ?? false,
          auctionForShop: item.auctionForShop ?? '',
          mercariUserId: item.mercariUserId ?? '',
          isUrgencyAnchor: item.isUrgencyAnchor ?? false,
          isSmileNameInputRequire: item.isSmileNameInputRequire ?? true,
          lineGroupId: item.lineGroupId ?? '',
          lineGroupName: item.lineGroupName ?? '',
          dispatchLineId: item.dispatchLineId ?? '',
          dispatchSheetId: item.dispatchSheetId ?? '',
          dispatchSheetTab: item.dispatchSheetTab ?? '',
          balanceSheetId: item.balanceSheetId ?? '',
          balanceSheetTab: item.balanceSheetTab ?? '',
          googleSheetId: item.googleSheetId ?? '',
          googleSheetTab: item.googleSheetTab ?? '',
          sheetSource: item.sheetSource ?? 'line',
          requireLineGroupLink: item.requireLineGroupLink ?? false,
          trackingSyncEnabled: item.trackingSyncEnabled ?? false,
          trackingUrlColumn: item.trackingUrlColumn ?? '',
          trackingColumn: item.trackingColumn ?? '',
          trackingDataStartRow: item.trackingDataStartRow ?? 2,
        });
        setModalTitle('Update Order By Option');
        setMode('edit');
      }
    } else {
      setOrderByOption({
        ...BLANK,
        sortOrder: orderByOptions.length > 0 ? Math.max(...orderByOptions.map((o) => o.sortOrder || 0)) + 1 : 0,
      });
      setMode('register');
      setModalTitle('Register Order By Option');
    }
    setIsFormOpen(true);
  };

  const closeForm = () => setIsFormOpen(false);

  const openConfirm = (id) => {
    const item = orderByOptions.find((o) => o.id === id);
    if (item) {
      setOrderByOption(item);
      setIsConfirmOpen(true);
    }
  };

  const closeConfirm = () => setIsConfirmOpen(false);

  // Old fee fields and feeTierRows are not sent: the backend keeps what it has /
  // gets feeTiers (null = no fee). operationWindows is no longer edited here.
  const buildPayload = () => {
    const { feeTierRows, specialFeeTierRows = [], feeRate, feeFixedJpy, feePercentage, ...rest } = orderByOption;
    return {
      ...rest,
      feeTiers: feeTierRows.length > 0 ? toTiers(feeTierRows) : null,
      specialFeeTiers: specialFeeTierRows.length > 0 ? toTiers(specialFeeTierRows) : null,
    };
  };

  const tiersProblem = () => {
    const problem = rowsError(orderByOption.feeTierRows)
      || ((orderByOption.specialFeeTierRows || []).length > 0 ? rowsError(orderByOption.specialFeeTierRows) : null);
    if (problem) toast.error(problem);
    return Boolean(problem);
  };

  const handleRegister = async () => {
    if (tiersProblem()) return;
    try {
      const result = await addOrderByOption(buildPayload());
      if (result) {
        toast.success('Order By option registered successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  // Owner 2026-11-01: a buyer's LINE ID changed without anyone noticing and their cards stopped.
  // Changing or clearing one of these on an existing record shows old → new and asks first.
  const SENSITIVE = [
    ['dispatchLineId', 'Buyer LINE (Chatcone socialId)'],
    ['lineGroupId', 'LINE group ID'],
    ['dispatchSheetId', 'Dispatch bot sheet ID'],
    ['dispatchSheetTab', 'Dispatch bot sheet tab'],
    ['googleSheetId', 'Sheet ID'],
    ['googleSheetTab', 'Sheet tab'],
  ];
  const confirmSensitiveChanges = (payload) => {
    const before = orderByOptions.find((o) => o.id === orderByOption.id) || {};
    const text = (v) => (v ?? '').toString().trim();
    const changed = SENSITIVE.filter(([f]) => text(before[f]) !== '' && text(before[f]) !== text(payload[f]));
    if (changed.length === 0) return true;
    const lines = changed.map(([f, label]) => `• ${label}: "${text(before[f])}" → "${text(payload[f]) || '(ว่าง)'}"`).join('\n');
    return window.confirm(`คุณกำลังจะเปลี่ยนข้อมูลสำคัญของ ${before.label || orderByOption.label}:\n\n${lines}\n\nถ้าผิด การ์ด/ชีทของคนกดคนนี้จะส่งไม่ถึง — ยืนยันไหม?`);
  };

  const handleUpdate = async () => {
    if (tiersProblem()) return;
    const payload = buildPayload();
    if (!confirmSensitiveChanges(payload)) return;
    try {
      await editOrderByOption(orderByOption.id, payload);
      toast.success('Order By option updated successfully!');
      closeForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteOrderByOption(id);
      toast.success('Order By option deleted successfully!');
      closeConfirm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  const handleToggleSuspend = async (id, enable) => {
    try {
      setTogglingBanId(id);
      await toggleSuspend(id, enable);
      toast.success(enable
        ? 'บัญชีถูก suspend แล้ว — ระบบหยุดแนะนำและนับ limit เป็น used ทั้งหมด'
        : 'ยกเลิก suspend เรียบร้อย');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'เปลี่ยนสถานะ suspend ไม่สำเร็จ');
    } finally {
      setTogglingBanId(null);
    }
  };

  const handleSyncTrackingNow = async () => {
    try {
      setIsSyncing(true);
      const { data } = await OrderByOptionService.runTrackingSyncNow();
      toast.success(`ซิงค์ tracking เสร็จแล้ว — อัปเดต ${data.updated} รายการ, ล้มเหลว ${data.failed}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ซิงค์ tracking ไม่สำเร็จ');
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    refreshOrderByOptions();
  }, []);

  return (
    <div>
      <Modal isOpen={isFormOpen} onRequestClose={closeForm} style={customStyles} ariaHideApp={false}>
        <OrderByOptionForm
          name={modalTitle}
          data={orderByOption}
          mode={mode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          onTiersChange={(rows) => setOrderByOption((prev) => ({ ...prev, feeTierRows: rows }))}
          onSpecialTiersChange={(rows) => setOrderByOption((prev) => ({ ...prev, specialFeeTierRows: rows }))}
          handleRegister={handleRegister}
          handleUpdate={handleUpdate}
          submitting={isLoading}
        />
      </Modal>
      <Modal isOpen={isConfirmOpen} onRequestClose={closeConfirm} style={customStyles} ariaHideApp={false}>
        <DeleteConfirm
          items={DELETE_ITEMS}
          data={orderByOption}
          closeConfirm={closeConfirm}
          confirmFunction={() => handleDelete(orderByOption.id)}
        />
      </Modal>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-5xl">Order By Setting</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSyncTrackingNow}
                disabled={isSyncing}
                className="bg-blue-500 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded"
              >
                {isSyncing ? 'Syncing…' : 'Sync Tracking Now'}
              </button>
              <button
                onClick={() => openForm(false)}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Add New
              </button>
            </div>
          </div>
          <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th scope="col" className="py-4 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">No</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Value</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Label</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Sort Order</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900" title="Fee tiers; orange = still on the old fee fields">Fee</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Active</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Limit/day</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Amount/day (¥)</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900" title="Old routing only. Buyer hours: Setting → Dispatch Bot">Windows (old)</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Flags</th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Suspend</th>
                    <th scope="col" className="relative py-4 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {orderByOptions.map((item, index) => {
                    const fee = feeSummary(item);
                    return (
                    <tr key={item.id} className={item.isSuspended ? 'bg-red-50' : ''}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{index + 1}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.value}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.label}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.sortOrder}</td>
                      <td className={`whitespace-nowrap px-3 py-4 text-sm ${fee.old ? 'text-orange-600' : 'text-gray-700'}`}>
                        {fee.text === 'none' ? <span className="text-gray-300">-</span> : fee.lines.map((line) => <div key={line}>{line}</div>)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {item.isActive ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Inactive</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {item.orderByType || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {item.dailyOrderLimit != null ? item.dailyOrderLimit : <span className="text-gray-300">∞</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {item.dailyAmountLimitJpy != null
                          ? `¥${Number(item.dailyAmountLimitJpy).toLocaleString()}${item.isAmountLimitStrict === false ? ' (soft)' : ''}`
                          : <span className="text-gray-300">∞</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {Array.isArray(item.operationWindows) && item.operationWindows.length > 0 ? (
                          <span className="text-xs font-mono text-blue-700">
                            {item.operationWindows.map(w => `${w.start}–${w.end}`).join(', ')}
                          </span>
                        ) : (
                          <span className="text-gray-300">ตลอดเวลา</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <div className="flex flex-col gap-0.5">
                          {item.isUrgencyAnchor && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-800">Urgency⚓</span>
                          )}
                          {!item.isSmileNameInputRequire && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">No SmileName</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {item.orderByType === 'mercariAccount' ? (
                          <div className="flex flex-col items-start gap-1">
                            {item.isSuspended ? (
                              <>
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">SUSPENDED</span>
                                <button
                                  disabled={togglingBanId === item.id}
                                  onClick={() => handleToggleSuspend(item.id, false)}
                                  className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:cursor-not-allowed underline"
                                >
                                  {togglingBanId === item.id ? 'กำลังดำเนินการ...' : 'ยกเลิก suspend'}
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Normal</span>
                                <button
                                  disabled={togglingBanId === item.id}
                                  onClick={() => handleToggleSuspend(item.id, true)}
                                  className="text-xs text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed underline"
                                >
                                  {togglingBanId === item.id ? 'กำลังดำเนินการ...' : 'Suspend'}
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-0 text-right text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <PencilIcon onClick={() => openForm(true, item.id)} className="cursor-pointer flex items-center w-6 h-6 fill-green-600" />
                          <TrashIcon onClick={() => openConfirm(item.id)} className="cursor-pointer flex items-center w-6 h-6 fill-red-600" />
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderByOptionSetting;
